'use client'

import React, { useEffect, useRef, useState } from 'react'

import { Box, SystemStyleObject, chakra } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { CopyIcon, ShareIcon } from '../Icon'
import { Tooltip } from '../Tooltip'
import {
  TOOLTIP_CLOSE_DELAY_MS,
  containerStyles,
  copyButtonStyles,
  copyIconStyles,
  copyTooltipContentStyles,
  dividerStyles,
  itemStyles,
} from './LocationBreadcrumb.styles'

export type BreadcrumbItem = {
  label: string
  onClick?: () => void
  href?: string
  /** When true, the crumb can be copied to the clipboard. A static crumb copies
   * on click; a link crumb keeps navigating and exposes a hover copy button —
   * mirroring the legacy drawer breadcrumb's click-to-copy affordance. */
  copyable?: boolean
  /** Text copied when `copyable` — defaults to `label`. */
  copyText?: string
  /**
   * Hover text for the crumb, shown in an interactive tooltip whose whole body
   * copies that text on click. Use it when the label is an abbreviation the
   * tooltip needs to spell out — e.g. a facility short code (ENG-32089).
   *
   * This is a distinct affordance from `copyable`, which copies from the crumb
   * itself (static) or a hover copy button (link) without spelling anything
   * out. A crumb picks one: `tooltipLabel` takes precedence when both are set.
   */
  tooltipLabel?: string
  /** Style overrides for this crumb only (e.g. emphasise the leading name). */
  styleOverrides?: SystemStyleObject
}

export type LocationBreadcrumbProps = {
  items: BreadcrumbItem[]
  colorScheme?: ColorScheme
  /** Confirmation shown in the tooltip after a copyable crumb is copied. */
  copiedLabel?: string
  /** Accessible name for the copy control. */
  copyLabel?: string
  /** Called after any crumb is copied — e.g. to raise a toast. */
  onCopied?: (copyText: string) => void
  /**
   * Reported when the clipboard write fails. This package has no Sentry, so
   * the consumer owns the capture rather than the failure being swallowed.
   */
  onCopyError?: (error: unknown) => void
  /** Style overrides applied to the flex container. */
  containerStyleOverrides?: SystemStyleObject
  /** Style overrides applied to every crumb. */
  itemStyleOverrides?: SystemStyleObject
  /** Style overrides applied to every divider. */
  dividerStyleOverrides?: SystemStyleObject
}

const COPIED_RESET_MS = 2000

/** Shared copy-to-clipboard state: writes `copyText`, flips `hasCopied` for a
 * confirmation window, and notifies `onCopied` (e.g. to toast). */
const useCopyToClipboard = (
  copyText: string,
  onCopied?: (copyText: string) => void,
  onCopyError?: (error: unknown) => void
) => {
  const [hasCopied, setHasCopied] = useState(false)
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current)
    }
  }, [])

  const copy = async () => {
    if (!navigator?.clipboard?.writeText) return
    try {
      await navigator.clipboard.writeText(copyText)
      setHasCopied(true)
      onCopied?.(copyText)
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current)
      resetTimerRef.current = setTimeout(
        () => setHasCopied(false),
        COPIED_RESET_MS
      )
    } catch (error) {
      // Clipboard access can be denied (permissions / insecure context). The
      // crumb simply doesn't confirm; consumers that care about the failure
      // opt in via `onCopyError` rather than this package surfacing an error.
      onCopyError?.(error)
    }
  }

  return { hasCopied, copy, setHasCopied }
}

/** Link element props (anchor vs button) with modifier-aware navigation. */
const getLinkProps = (item: BreadcrumbItem) => ({
  ...(item.href
    ? { as: 'a' as const, href: item.href }
    : { as: 'button' as const }),
  onClick: item.href
    ? (e: React.MouseEvent) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return
        e.preventDefault()
        item.onClick?.()
      }
    : item.onClick,
})

type CrumbProps = {
  item: BreadcrumbItem
  copiedLabel: string
  copyLabel: string
  colorScheme: ColorScheme
  style: SystemStyleObject
  onCopied?: (copyText: string) => void
  onCopyError?: (error: unknown) => void
}

/** Static crumb that copies its text to the clipboard on click. */
const CopyableCrumb = ({
  item,
  copiedLabel,
  colorScheme,
  style,
  onCopied,
  onCopyError,
}: CrumbProps) => {
  const copyText = item.copyText ?? item.label
  const anchorRef = useRef<HTMLButtonElement>(null)
  const [isHovering, setIsHovering] = useState(false)
  const { hasCopied, copy } = useCopyToClipboard(
    copyText,
    onCopied,
    onCopyError
  )

  return (
    <>
      <chakra.button
        onClick={copy}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        ref={anchorRef}
        type='button'
        {...style}
      >
        {item.label}
      </chakra.button>
      {(isHovering || hasCopied) && (
        <Tooltip
          anchorEl={anchorRef.current}
          colorScheme={colorScheme}
          label={hasCopied ? copiedLabel : copyText}
          placement='bottom'
        />
      )}
    </>
  )
}

/** Link crumb that navigates on click and exposes a copy button on hover or
 * keyboard focus. The button stays in the tab order / accessibility tree when
 * idle (opacity + pointer-events, not `visibility: hidden`, which would hide it
 * from keyboard and screen-reader users) and is revealed when the crumb is
 * hovered or focused-within. */
const CopyableLinkCrumb = ({
  item,
  copiedLabel,
  copyLabel,
  colorScheme,
  style,
  onCopied,
  onCopyError,
}: CrumbProps) => {
  const copyText = item.copyText ?? item.label
  const iconRef = useRef<HTMLButtonElement>(null)
  const [isHovering, setIsHovering] = useState(false)
  const [isFocusWithin, setIsFocusWithin] = useState(false)
  const { hasCopied, copy } = useCopyToClipboard(
    copyText,
    onCopied,
    onCopyError
  )

  const isRevealed = isHovering || isFocusWithin || hasCopied

  return (
    <Box
      alignItems='center'
      as='span'
      display='inline-flex'
      gap='4px'
      onBlur={(e) => {
        // Only collapse when focus leaves the crumb entirely (not when moving
        // between the link and the copy button).
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setIsFocusWithin(false)
        }
      }}
      onFocus={() => setIsFocusWithin(true)}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <Box {...getLinkProps(item)} {...style}>
        {item.label}
      </Box>
      <chakra.button
        aria-label={copyLabel}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          copy()
        }}
        opacity={isRevealed ? 1 : 0}
        // Stays focusable (in the tab order) while visually hidden; only the
        // pointer target is suppressed until revealed.
        pointerEvents={isRevealed ? 'auto' : 'none'}
        ref={iconRef}
        transition='opacity 0.12s ease'
        type='button'
        {...copyButtonStyles(colorScheme)}
      >
        <CopyIcon color='currentColor' height={14} width={14} />
      </chakra.button>
      {hasCopied && (
        <Tooltip
          anchorEl={iconRef.current}
          colorScheme={colorScheme}
          label={copiedLabel}
          placement='bottom'
        />
      )}
    </Box>
  )
}

/**
 * Crumb whose hover tooltip spells out `tooltipLabel` — the full name behind a
 * truncated or abbreviated crumb — and copies it when the tooltip body is
 * clicked (ENG-32089). Owns its own hover state, anchor ref and timers:
 * `Tooltip` is anchor-positioned, so sharing any of them across the mapped
 * list would point every tooltip at whichever crumb rendered last.
 */
const TooltipCopyCrumb = ({
  item,
  copiedLabel,
  copyLabel,
  colorScheme,
  style,
  onCopied,
  onCopyError,
}: CrumbProps) => {
  const tooltipLabel = item.tooltipLabel ?? item.label
  const [isOpen, setIsOpen] = useState(false)
  const anchorRef = useRef<HTMLElement | null>(null)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isLink = Boolean(item.onClick || item.href)
  const { hasCopied, copy, setHasCopied } = useCopyToClipboard(
    tooltipLabel,
    onCopied,
    onCopyError
  )

  useEffect(
    () => () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
    },
    []
  )

  // Hovering either the crumb or the tooltip keeps it open, so the pointer can
  // travel between them to reach the copy button.
  const openTooltip = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
    setIsOpen(true)
  }

  const scheduleClose = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null
      setIsOpen(false)
      setHasCopied(false)
    }, TOOLTIP_CLOSE_DELAY_MS)
  }

  return (
    <>
      <Box
        {...(isLink ? getLinkProps(item) : { as: 'span' as const })}
        {...style}
        onMouseEnter={openTooltip}
        onMouseLeave={scheduleClose}
        ref={anchorRef}
      >
        {item.label}
      </Box>
      {/* Portalled, so it adds no flex item to the breadcrumb row. */}
      {isOpen && (
        <Tooltip
          anchorEl={anchorRef.current}
          colorScheme={colorScheme}
          isInteractive
          label={
            <Box
              {...copyTooltipContentStyles}
              aria-label={copyLabel}
              as='button'
              onClick={copy}
            >
              <Box as='span'>{hasCopied ? copiedLabel : tooltipLabel}</Box>
              <Box {...copyIconStyles(colorScheme, hasCopied)} as='span'>
                <ShareIcon color='currentColor' size={12} />
              </Box>
            </Box>
          }
          onMouseEnter={openTooltip}
          onMouseLeave={scheduleClose}
          placement='bottom'
          variant='description'
        />
      )}
    </>
  )
}

export const LocationBreadcrumb = ({
  items,
  colorScheme = 'dark',
  copiedLabel = 'Copied',
  copyLabel = 'Copy',
  onCopied,
  onCopyError,
  containerStyleOverrides,
  itemStyleOverrides,
  dividerStyleOverrides,
}: LocationBreadcrumbProps) => {
  return (
    <Box {...containerStyles} {...containerStyleOverrides}>
      {items.map((item, index) => {
        const isLink = Boolean(item.onClick || item.href)
        const hasTooltip = Boolean(item.tooltipLabel)
        const isCopyable = Boolean(item.copyable)
        const style = {
          ...itemStyles(colorScheme, {
            isLink,
            isClickable: isLink || isCopyable || hasTooltip,
          }),
          ...itemStyleOverrides,
          ...item.styleOverrides,
        }
        const crumbProps: CrumbProps = {
          item,
          copiedLabel,
          copyLabel,
          colorScheme,
          style,
          onCopied,
          onCopyError,
        }
        return (
          // Keyed by position, not label alone: each crumb owns hover state, an
          // anchor ref and timers, so two crumbs sharing a label (a device
          // called "HQ" next to facility "HQ") would collide and mis-anchor the
          // tooltip.
          <React.Fragment key={`${index}-${item.label}`}>
            {hasTooltip ? (
              <TooltipCopyCrumb {...crumbProps} />
            ) : isCopyable && isLink ? (
              <CopyableLinkCrumb {...crumbProps} />
            ) : isCopyable ? (
              <CopyableCrumb {...crumbProps} />
            ) : isLink ? (
              <Box {...getLinkProps(item)} {...style}>
                {item.label}
              </Box>
            ) : (
              <Box as='span' {...style}>
                {item.label}
              </Box>
            )}
            {index < items.length - 1 && (
              <Box {...dividerStyles(colorScheme)} {...dividerStyleOverrides} />
            )}
          </React.Fragment>
        )
      })}
    </Box>
  )
}
